/**
 * [INPUT]: 依赖 @radix-ui/react-slot 的 asChild 兼容能力、class-variance-authority、共享 32px 控件尺寸与抬升材质
 * [OUTPUT]: 对外提供 Button 组件、buttonVariants 样式函数
 * [POS]: zinnercms-design-system/ui 的按钮真相源，默认变体复刻黑色金属主按钮并保持旧 shadcn API；默认 type=button 防表单误提交
 * [PROTOCOL]: 变更时更新此头部，然后检查 CLAUDE.md
 */
import { Slot } from "@radix-ui/react-slot"
import { cva } from "class-variance-authority";

import { cn } from "@/zinnercms-design-system/lib/utils"
import { controlSizes, raisedControlSurface } from "@/zinnercms-design-system/ui/foundations"

const buttonVariants = cva(
  "relative isolate inline-flex shrink-0 cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md border text-[13px] font-semibold leading-none tracking-[-0.01em] outline-none transition-[filter,box-shadow,background-color,border-color,color] duration-200 ease-settle disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-45 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 focus-visible:ring-[3px] focus-visible:ring-ring/30 aria-invalid:border-destructive aria-invalid:ring-[3px] aria-invalid:ring-destructive/20",
  {
    variants: {
      variant: {
        default:
          "border-[var(--zncms-metal-stroke-mid)] !bg-[linear-gradient(to_bottom,var(--zncms-metal-top),var(--zncms-metal-bottom))] !text-primary-foreground shadow-[inset_0_1px_0_var(--zncms-metal-stroke-top),inset_0_-1px_0_var(--zncms-metal-stroke-bottom),var(--zncms-metal-shadow)] hover:brightness-110 hover:shadow-[inset_0_2px_0_var(--zncms-metal-stroke-mid),inset_0_-1px_0_var(--zncms-metal-stroke-bottom),var(--zncms-metal-shadow-hover)] active:brightness-95 active:shadow-[inset_0_2px_2px_var(--zncms-metal-stroke-bottom),inset_0_-1px_0_var(--zncms-metal-stroke-top)] [&>svg]:size-6 [&>svg]:rounded-sm [&>svg]:border [&>svg]:border-border [&>svg]:bg-[linear-gradient(to_bottom,var(--zncms-metal-icon-top),var(--zncms-metal-icon-bottom))] [&>svg]:p-1 [&>svg]:text-[var(--zncms-metal-icon-foreground)] [&>svg]:shadow-xs [&>svg]:transition-[filter,box-shadow] [&>svg]:duration-200 hover:[&>svg]:brightness-105 hover:[&>svg]:shadow-md",
        destructive:
          "border-destructive !bg-destructive !text-destructive-foreground shadow-xs hover:brightness-105 hover:shadow-lg active:brightness-95 active:shadow-xs",
        outline:
          cn(raisedControlSurface, "!bg-background !text-foreground hover:!bg-card"),
        secondary:
          cn(raisedControlSurface, "border-border !bg-secondary !text-secondary-foreground hover:brightness-105"),
        ghost:
          "border-transparent !bg-transparent !text-foreground shadow-none hover:!bg-accent hover:!text-accent-foreground hover:shadow-sm active:brightness-95 active:shadow-none",
        link: "border-transparent !bg-transparent !text-foreground shadow-none underline-offset-4 hover:underline",
      },
      size: {
        default: `${controlSizes.default} px-4 has-[>svg]:pl-1.5 has-[>svg]:pr-3`,
        sm: `${controlSizes.sm} gap-1.5 px-3 has-[>svg]:pl-1 has-[>svg]:pr-2.5`,
        lg: `${controlSizes.lg} px-5 text-sm has-[>svg]:pl-1.5 has-[>svg]:pr-4`,
        icon: `${controlSizes.icon} p-1`,
        "icon-sm": `${controlSizes.iconSm} p-1`,
        "icon-lg": `${controlSizes.iconLg} p-1`,
      },
    },
    compoundVariants: [
      { variant: "ghost", size: "icon", className: cn(raisedControlSurface, "!bg-background") },
      { variant: "ghost", size: "icon-sm", className: cn(raisedControlSurface, "!bg-background") },
      { variant: "ghost", size: "icon-lg", className: cn(raisedControlSurface, "!bg-background") },
    ],
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

function Button({
  className,
  variant = "default",
  size = "default",
  asChild = false,
  type,
  ...props
}) {
  // 原生 <button> 默认 type="submit", 在意外的 form 上下文里会触发整页 reload.
  // 这里强制默认 type="button", 调用方需要 submit 必须显式声明 type="submit".
  const resolvedType = asChild ? type : (type ?? "button")
  const Comp = asChild ? Slot : "button"
  return (
    <Comp
      data-slot="button"
      data-variant={variant}
      data-size={size}
      type={resolvedType}
      aria-label={props["aria-label"] ?? (String(size).startsWith("icon") ? "button" : undefined)}
      className={cn(buttonVariants({ variant, size, className }))}
      {...props} />
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export { Button, buttonVariants }
